import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowUpCircle, CheckCircle2, Loader2, RotateCw } from 'lucide-react';
import { config } from '@/config.ts';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import {
  LIVE_PROGRESS_MS,
  fetchGatewaySoftwareVersions,
  retryInFlight,
  setGatewaySoftwareVersion,
  type GatewayUpdateProgress,
  type UpdatePolicy,
} from '@/features/gateway-version';

/** The server's policy + pinned version → the unified selector value. */
function serverSelection(policy: UpdatePolicy, pinnedVersion: string | null): string {
  if (policy === 'pinned' && pinnedVersion) return `pin:${pinnedVersion}`;
  if (policy === 'hold') return 'hold';
  return 'auto';
}

/** A selector value → the policy + version to send. */
function selectionToPolicy(selection: string): { policy: UpdatePolicy; version?: string } {
  if (selection === 'hold') return { policy: 'hold' };
  if (selection.startsWith('pin:')) return { policy: 'pinned', version: selection.slice('pin:'.length) };
  return { policy: 'auto' };
}

/**
 * Set a gateway's software version — the same control for container and VM
 * (ISO) gateways. One selector picks the version directly: Automatic tracks
 * the latest release (showing what that resolves to now); an exact version
 * pins it (up or down — a downgrade is a rollback); Hold never changes.
 * Shows the running version and, on failure, an incident-worthy status with a
 * Retry (docs/GatewayVersionManagement.md).
 */
export function GatewaySoftwareVersion({ gatewayId }: { gatewayId: string }) {
  const backendApi = config.backendApi;
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['gateway-software-versions', gatewayId, backendApi],
    // Stamped with when it arrived, so how old the progress is can be judged
    // without reading the clock while rendering.
    queryFn: async () => ({ ...(await fetchGatewaySoftwareVersions(gatewayId)), fetchedAt: Date.now() }),
  });

  const retrying = data ? retryInFlight(data, data.fetchedAt) : false;
  // Progress arrives every few seconds while the retry runs, and each message
  // refetches. When it stops, look again once the last one is too old to count,
  // so a retry that died mid-way goes back to showing the failure.
  const fetchedAt = data?.fetchedAt;
  useEffect(() => {
    if (!retrying) return;
    const timer = setTimeout(() => { void refetch(); }, LIVE_PROGRESS_MS);
    return () => { clearTimeout(timer); };
  }, [retrying, fetchedAt, refetch]);

  // One selector encodes policy AND pinned version, so a version is chosen
  // directly (no separate "pin" step):
  //   'auto'      → track the latest published version
  //   'hold'      → freeze on the current version
  //   'pin:<ver>' → pinned to an exact version
  const [selection, setSelection] = useState<string>('auto');

  // Sync local form to the server state once loaded / after a change.
  const [synced, setSynced] = useState<typeof data>(undefined);
  if (data !== synced) {
    setSynced(data);
    if (data) setSelection(serverSelection(data.policy, data.pinnedVersion));
  }

  const mutation = useMutation({
    mutationFn: setGatewaySoftwareVersion,
    onSuccess: () => {
      toast.success('Version policy updated — the gateway will apply it shortly.');
      void queryClient.invalidateQueries({ queryKey: ['gateway-software-versions', gatewayId] });
    },
    onError: () => { toast.error('Failed to update version policy.'); },
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading version info…</p>;
  if (isError || !data) return <p className="text-sm text-muted-foreground">Version management is unavailable for this gateway.</p>;

  // The API returns availableVersions newest-first, so [0] is what
  // "automatic" would resolve to right now.
  const latestVersion = data.availableVersions[0];
  const running = data.runningVersion ?? 'unknown';
  // A pin that hasn't converged yet, or a permanent failure — unless a retry
  // of the failed version is visibly under way (see `retryInFlight`).
  const phase = retrying ? 'converging' : data.statusPhase;

  const derived = selectionToPolicy(selection);
  const canApply = selection !== serverSelection(data.policy, data.pinnedVersion);
  const applyDisabled = mutation.isPending || !canApply;

  const apply = () => {
    mutation.mutate({ gatewayId, policy: derived.policy, version: derived.version });
  };

  // Re-apply the *current server* desired state: the backend bumps
  // pinned_set_at and re-sends CheckSoftwareUpdateNow, nudging the gateway to
  // check + apply immediately. Shared by "Update now" and "Retry".
  const reapply = () => {
    mutation.mutate({
      gatewayId,
      policy: data.policy,
      version: data.policy === 'pinned' ? data.pinnedVersion ?? undefined : undefined,
    });
  };

  // The version the current server policy resolves to (auto → latest,
  // pinned → the pin; hold has no target).
  const resolvedTarget =
    data.policy === 'auto' ? latestVersion : data.policy === 'pinned' ? data.pinnedVersion ?? undefined : undefined;
  // An available update the gateway hasn't picked up yet: the desired version
  // is newer than what's running, and it's not mid-apply or failed. Versions
  // come newest-first, so a lower index = newer. Without this, auto mode can
  // sit "up to date" until the gateway's next scheduled check even though a
  // newer version is out.
  const runningIdx = data.availableVersions.indexOf(running);
  const targetIdx = resolvedTarget ? data.availableVersions.indexOf(resolvedTarget) : -1;
  const updateAhead =
    !!resolvedTarget &&
    resolvedTarget !== running &&
    phase !== 'converging' &&
    phase !== 'failed' &&
    targetIdx !== -1 &&
    (runningIdx === -1 || targetIdx < runningIdx);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold">Deploy a version</h3>
        <p className="text-sm text-muted-foreground">
          Running <code className="font-mono text-xs">{running}</code>
          {data.deploymentKind === 'vyos_iso' ? ' · applying a version reboots the appliance' : ''}
        </p>
      </div>

      <StatusLine
        phase={phase}
        statusVersion={data.statusVersion}
        statusError={data.statusError}
        running={running}
        progress={data.progress}
        updateTarget={updateAhead ? resolvedTarget : null}
        deploymentKind={data.deploymentKind}
      />

      <div className="space-y-1">
        <Label htmlFor="version-select">Version</Label>
        <Select value={selection} onValueChange={setSelection}>
          <SelectTrigger id="version-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {/* Automatic tracks the latest, and shows what that resolves to
                now — distinct from pinning that same version explicitly. */}
            <SelectItem value="auto">
              Automatic (latest{latestVersion ? `, ${latestVersion}` : ''})
            </SelectItem>
            {data.availableVersions.map((v) => (
              <SelectItem key={v} value={`pin:${v}`}>
                {v}
                {v === running ? ' (running)' : ''}
              </SelectItem>
            ))}
            <SelectItem value="hold">Hold (don&apos;t change)</SelectItem>
          </SelectContent>
        </Select>
        {data.availableVersions.length === 0 ? (
          <p className="text-xs text-muted-foreground">No published versions available for this gateway.</p>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={apply} disabled={applyDisabled} className="gap-1.5">
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Apply
        </Button>
        {updateAhead && !canApply ? (
          // The desired version is newer than what's running and there's no
          // unsaved policy change to Apply — offer an explicit trigger instead
          // of waiting for the gateway's next scheduled check.
          <Button className="gap-1.5" disabled={mutation.isPending} onClick={reapply}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpCircle className="h-4 w-4" />}
            Update now
          </Button>
        ) : null}
        {phase === 'failed' ? (
          <Button variant="outline" className="gap-1.5" disabled={mutation.isPending} onClick={reapply}>
            <RotateCw className="h-4 w-4" />
            Retry
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function StatusLine({
  phase,
  statusVersion,
  statusError,
  running,
  progress,
  updateTarget,
  deploymentKind,
}: {
  phase: string | null;
  statusVersion: string | null;
  statusError: string | null;
  running: string;
  /** Live install progress, or null — see the `converging` branch. */
  progress: GatewayUpdateProgress | null;
  /** Set when a newer desired version is available but not yet applied. */
  updateTarget: string | null;
  deploymentKind: string;
}) {
  if (phase === 'failed' && deploymentKind === 'vyos_iso') {
    // An appliance's failure is as often a download that stopped as an image
    // that would not boot, and either way it tries again: every update check
    // that is offered the version installs it (the daily one, the one on each
    // reconnect).
    return (
      <div className="flex items-start gap-2 rounded-md border border-sev-1/40 bg-sev-1/10 px-3 py-2 text-sm">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-sev-1" />
        <div>
          <p className="font-medium">Updating to {statusVersion ?? 'unknown'} failed.</p>
          <p className="text-muted-foreground">
            {statusError ? `${sentence(statusError)} ` : ''}
            The appliance tries again at its next update check, or you can retry now.
          </p>
        </div>
      </div>
    );
  }
  if (phase === 'failed') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-sev-1/40 bg-sev-1/10 px-3 py-2 text-sm">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-sev-1" />
        <div>
          <p className="font-medium">Version {statusVersion ?? 'unknown'} failed to run.</p>
          <p className="text-muted-foreground">
            The gateway rolled back to {running} and won’t retry on its own.
            {statusError ? ` ${statusError}` : ''}
          </p>
        </div>
      </div>
    );
  }
  if (phase === 'converging') {
    // `progress` is what the gateway is doing right now; it arrives only while
    // an install is genuinely in flight. Falling back to the bare "Applying
    // version…" is the whole compatibility story — a gateway too old to report
    // progress, or an apply that has not reached its first phase yet, reads
    // exactly as it did before (docs/GatewayUpdateProgress.md).
    //
    // `label` is authored by the gateway so the wording can change without a
    // frontend release; `phase` is the stable id and is what to key off.
    const headline = progress?.label
      ? `${progress.label} ${progress.version}`.trim()
      : `Applying version ${statusVersion ?? ''}…`;
    // `percent` is null when the download size is unknown, which is not 0 —
    // a bar pinned at zero reads as "stuck", so show none at all.
    const percent = progress?.percent ?? null;
    return (
      <div className="flex items-start gap-2 rounded-md border border-border-subtle bg-accent/20 px-3 py-2 text-sm">
        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="font-medium">{headline}</p>
          {progress?.detail ? (
            <p className="text-xs text-muted-foreground">{progress.detail}</p>
          ) : null}
          {percent !== null ? (
            <Progress value={percent} className="h-1.5" aria-label={`${String(percent)}% complete`} />
          ) : null}
        </div>
      </div>
    );
  }
  if (updateTarget) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-accent/20 px-3 py-2 text-sm">
        <ArrowUpCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span>
          Update available: <code className="font-mono text-xs">{updateTarget}</code>
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-accent/10 px-3 py-2 text-sm">
      <CheckCircle2 className="h-4 w-4 shrink-0 text-sev-5" />
      <span>Up to date.</span>
    </div>
  );
}

/** A gateway's error as a sentence: capitalised, and ending in a stop. */
function sentence(text: string): string {
  const trimmed = text.trim();
  const capitalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}
